const LINKEDIN_PROFILE_PATH = /^\/in\/([^/?#]+)/;

/**
 * Parses a URL, defaulting to https when the scheme was left off.
 *
 * @param {string} url
 * @returns {URL|null}
 */
function parseUrl(url) {
    let value = String(url || '').trim();
    if (!value) return null;

    try {
        return new URL(value.includes('://') ? value : `https://${value}`);
    } catch (e) {
        return null;
    }
}

/**
 * Returns true when the URL is hosted on linkedin.com or one of its country
 * subdomains, ie. uk.linkedin.com.
 *
 * The host is matched rather than searched for anywhere in the string, so
 * lookalike domains such as notlinkedin.com/in/foo do not pass.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isLinkedInHost(url) {
    let parsed = parseUrl(url);
    if (!parsed) return false;

    let host = parsed.hostname.toLowerCase();

    return host === 'linkedin.com' || host.endsWith('.linkedin.com');
}

/**
 * Extracts the profile handle from a LinkedIn profile URL.
 *
 * @param {string} url
 * @returns {string|null} The lower-cased handle, or null when not a profile URL
 */
export function getLinkedInProfileHandle(url) {
    if (!isLinkedInHost(url)) return null;

    let matches = LINKEDIN_PROFILE_PATH.exec(parseUrl(url).pathname);
    if (!matches) return null;

    let handle = matches[1];
    try {
        handle = decodeURIComponent(handle);
    } catch (e) {
        // Leave the handle as-is when it is not valid percent encoding
    }

    return handle.toLowerCase();
}

/**
 * Returns true when the URL points at a LinkedIn member profile.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isLinkedInProfileUrl(url) {
    return !!getLinkedInProfileHandle(url);
}

/**
 * Rebuilds a LinkedIn profile URL from its handle.
 *
 * Rebuilding drops any tracking parameters (utm_*, lipi session tokens) that
 * were copied along with the link, and stores the www subdomain so the link
 * keeps working when the member changes the country on their profile.
 *
 * @param {string} url
 * @returns {string|null}
 */
export function canonicalLinkedInProfileUrl(url) {
    let handle = getLinkedInProfileHandle(url);

    return handle ? `https://www.linkedin.com/in/${handle}` : null;
}

/**
 * The formsy rule for a LinkedIn profile URL field.
 *
 * @param {Object} model
 * @param {string} value
 * @returns {boolean|string} true when valid, otherwise the error message
 */
export function linkedInProfileUrlRule(model, value) {
    if (!value) return true

    if (isLinkedInProfileUrl(value)) return true

    if (isLinkedInHost(value)) {
        return "Enter the personal LinkedIn profile (linkedin.com/in/...), not a company or feed page"
    }

    return "Enter a LinkedIn profile URL, ie. linkedin.com/in/janedoe"
}
